export const COPY = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      about: "About",
      testimonials: "Testimonials",
      podcast: "Podcast & Videos",
      booking: "Booking",
      bookCall: "Book a Free Discovery Call",
      bookCallFull: "Book a Free Discovery Call",
    },
    footer: {
      kicker: "The first step is free",
      titlePre: "Ready to work",
      titleEm: "with",
      titlePost: "your brain, not against it?",
      cta: "Book a Free Discovery Call",
      tagline:
        "Coaching for adults and teens with ADHD and executive dysfunction: systems, focus, and confidence that fit your brain.",
      explore: "Explore",
      links: {
        home: "Home",
        services: "Services",
        booking: "Book a Call",
        payments: "How to Pay",
        about: "About Me",
        testimonials: "Testimonials",
      },
      connect: "Connect",
      creds: "ICF Member · Certified Life Coach",
      copyright: "Copyright © 2026 Natalia Life Coach. All Rights Reserved.",
      note: "Warm, ADHD-informed coaching · Online sessions",
    },
    marquee: {
      items: [
        "Executive Function",
        "System Building",
        "Focus",
        "Self-Compassion",
        "Sustainable Growth",
        "ADHD-Informed Coaching",
      ],
    },
    bio: {
      mission:
        "My mission as a Life Coach is to empower and inspire my clients to live a life that is authentic, fulfilling, and aligned with their values. I believe that everyone has the potential to create positive change in their lives, and I am here to support you on your journey towards personal growth and transformation.",
      approach:
        "I take a collaborative and solution-focused approach to coaching. I'll help you identify your strengths, overcome obstacles, and create a practical plan that fits how your brain actually works. I offer one-on-one coaching sessions for adults and teens with ADHD and executive dysfunction.",
    },
    home: {
      heroBadge: "ADHD & Executive Function Coaching",
      heroL1: "Struggling with focus,",
      heroL2: "follow-through,",
      heroL3: "or overwhelm?",
      heroAccent: "You don't lack willpower.",
      heroSubPre: "Your brain just works differently, and that's exactly where I come in. I'm Natalia, a certified life coach helping adults and teens with ADHD and executive dysfunction build ",
      heroSubStrong: "systems, focus, and confidence",
      heroSubPost: " that actually fit how their brain works.",
      heroCtaPrimary: "Book a Free Discovery Call",
      heroCtaSecondary: "Explore Services",
      heroMicro: "Free · No payment required · Online, from anywhere",
      portraitRole: "ICF Member · Certified Coach",
      painKicker: "Sound familiar?",
      painTitle: "Why willpower was never the problem",
      painIntro:
        "Conventional productivity advice was designed for conventional brains. If planners, apps, and \"just do it\" have all failed you, nothing is wrong with you. The tools were wrong for you.",
      painHelpLabel: "How coaching helps",
      pain: {
        time: {
          title: "Time Blindness",
          struggle: "Hours vanish. Deadlines ambush you. It's not carelessness, it's how your brain perceives time.",
          reframe: "I build external time anchors: visual timers, buffers, and rhythms that make time visible.",
        },
        initiation: {
          title: "Task Initiation",
          struggle: "You know exactly what to do. You just... can't start. That's initiation paralysis, not laziness.",
          reframe: "I design friction-free first steps and body-doubling strategies that make starting feel safe.",
        },
        memory: {
          title: "Working Memory",
          struggle: "Walk into a room and forget why. Lose the thread mid-task. Your mental sticky notes keep falling off.",
          reframe: "I create external capture systems so nothing important ever lives only in your head.",
        },
      },
      chaptersKicker: "How coaching works",
      chaptersTitle: "A gentle roadmap, in four chapters",
      watch: {
        kicker: "Watch & Listen",
        title: "Life in HD, the podcast",
        intro:
          "Real conversations about ADHD, self-discovery, and building a life that fits your brain, hosted by Natalia.",
        seeAll: "See All Episodes",
        subscribe: "Subscribe on YouTube",
      },
      quotesKicker: "Client words",
      quotesTitle: "Real people. Real follow-through.",
      quotesLink: "Read all client stories",
    },
    chapters: [
      { num: "01", title: "Understand", text: "Map how your unique brain actually works: your strengths, your sticking points, and the patterns behind the overwhelm." },
      { num: "02", title: "Unpack", text: "Untangle the goals that matter from the noise, and break them into pieces small enough for a real brain to start on." },
      { num: "03", title: "Systematize", text: "Design ADHD-friendly systems for time, tasks, and follow-through, built around your life, not against it." },
      { num: "04", title: "Sustain", text: "Grow consistency with accountability and self-compassion, so progress survives even your hardest weeks." },
    ],
    ctaBanner: {
      kicker: "Free · No payment · Zero pressure",
      title: "Ready to build a system that works with your brain?",
      subtitle: "Start with a free, no-pressure discovery call. No payment, no commitment. Just a conversation.",
      button: "Book a Free Discovery Call",
    },
    testimonialsData: {
      lucas: {
        quote: "Natalia was instrumental in helping me cope with my ADHD diagnosis. She provided a clear plan and effective strategies that enabled me to complete projects I previously struggled with.",
        catLabel: "ADHD",
      },
      daniela: {
        quote: "Due to my ADHD I wasn’t able to finish any project, I couldn’t find the time to work on my dreams, but Natalia really helped me to focus on myself, and to really understand my brain!",
        catLabel: "Finishing Projects",
      },
      "given-cassia": {
        quote: "Natalia helped us get organized in our wedding planning process. She provided tips and useful tools to keep us organized. We liked how flexible and genuine she was in empowering us in our process.",
        catLabel: "Wedding Planning",
      },
      rosie: {
        quote: "Working with Natalia has been an absolute game-changer for me. Her understanding of ADHD and compassionate approach have helped me navigate challenges I once thought were insurmountable.",
        catLabel: "ADHD",
      },
    },
    services: {
      kicker: "Services",
      title: "Coaching designed for brains that think differently",
      intro: "I offer one-on-one coaching sessions for adults and teens with ADHD and executive dysfunction. My focus is on building practical systems for focus, follow-through, and daily life that actually fit how your brain works.",
      bookCta: "Book a Free Discovery Call",
      flagshipBadge: "Specialty",
      efLabel: "For executive dysfunction",
      offeringsIntro: "Within my one-on-one sessions, I work across these areas depending on what matters most to you.",
      offerings: [
        {
          id: "adhd-coaching",
          title: "ADHD & Executive Function Coaching",
          flagship: true,
          description: "My specialty. One-on-one coaching for adults and teens whose brains resist conventional productivity advice, building systems for focus, follow-through, and daily life that are designed around how your brain actually works.",
          helps: "Directly targets executive dysfunction: task initiation, time blindness, working memory, organization, and emotional regulation.",
        },
        {
          id: "stress-management",
          title: "Stress Management",
          description: "Learn to spot overload before it becomes shutdown, and build recovery rituals that calm a nervous system that's always running at full speed.",
          helps: "Reduces the chronic overwhelm that makes every executive task feel ten times heavier.",
        },
        {
          id: "work-life-balance",
          title: "Work-Life Balance",
          description: "Create boundaries and transitions that actually hold, especially when your brain struggles to switch off, switch tasks, or switch gears.",
          helps: "Eases task-switching difficulty and the hyperfocus crashes that blur work and rest together.",
        },
        {
          id: "career-transitions",
          title: "Career Transitions",
          description: "Navigate job changes, promotions, or entirely new paths with a structured plan, breaking big, vague decisions into clear, doable steps.",
          helps: "Turns decision paralysis into a sequence of small, scheduled actions with built-in accountability.",
        },
        {
          id: "personal-growth",
          title: "Personal Growth",
          description: "Reconnect with the goals and dreams that keep getting postponed, and rebuild self-trust after years of feeling 'behind'.",
          helps: "Rebuilds the confidence that executive dysfunction quietly erodes, one kept promise to yourself at a time.",
        },
      ],
      paySectionText: "Sessions are invoiced after each completed call. Nothing is ever charged upfront. Here's exactly how it works.",
      payLinkLabel: "How Payment Works",
      notSurePre: "Not sure it's a fit?",
      notSureLink: "Book a free discovery call",
      notSurePost: "and decide together.",
      faqKicker: "Questions",
      faqTitle: "Asked often, answered honestly",
      faqs: [
        { q: "Do I need an official ADHD diagnosis to work with Natalia?", a: "No. If you struggle with focus, follow-through, organization, or overwhelm, diagnosed or not, you are welcome. Coaching is about building systems that work for your brain, wherever you're starting from." },
        { q: "What happens in the free discovery call?", a: "A relaxed, friendly conversation. You share what's been hard, Natalia explains how coaching works, and you both decide if it's a good fit. No payment, no pressure, no preparation needed." },
        { q: "Are sessions online or in person?", a: "Sessions are held online via video call, so you can join from wherever you feel most comfortable. This works especially well for teens and busy adults." },
        { q: "How does payment work?", a: "Nothing is charged upfront. After each completed session, Natalia emails you an invoice with a link to the payments page, where you can pay by Interac e-Transfer, credit card, or Apple Pay / Google Pay." },
        { q: "What if I need to cancel or reschedule?", a: "Life happens. You can reschedule or cancel any session free of charge with at least 24 hours' notice through your booking link. Cancellations or reschedules made less than 24 hours before the session will incur a $50 CAD late cancellation fee." },
      ],
    },
    booking: {
      kicker: "Booking",
      title: "Pick a time. That's it.",
      intro: "One step, no accounts, no forms to fill out. Choose your call below and grab whatever time suits you.",
      introFree: "The discovery call is completely free.",
      chips: ["Zero pressure, zero prep", "No payment required", "Online video call"],
      tabs: {
        discovery: { label: "Free Discovery Call", hint: "30 min · free · no payment" },
        session: { label: "Coaching Session", hint: "60 min · for existing clients" },
      },
      fallbackPre: "Trouble with the calendar?",
      fallbackLink: "Email",
      fallbackPost: "and I'll find a time with you.",
    },
    payments: {
      kicker: "Payments",
      title: "How to pay your invoice",
      intro: "Nothing is ever charged upfront. Payment happens after each completed session, by invoice. Here's the simple flow.",
      stepsTitle: "How it works",
      steps: [
        "Book a free discovery call through the online calendar.",
        "Natalia emails you a contract (PDF) to sign.",
        "Book one or more 60-minute sessions at your own pace.",
        "After each completed session, Natalia emails you an invoice with a link back to this page.",
        "Pay using your preferred method below.",
      ],
      optionsTitle: "Choose how to pay",
      optionsIntro: "Use whichever is easiest for you. The amount on your invoice is all you ever pay.",
      options: [
        {
          id: "interac",
          title: "Interac e-Transfer",
          desc: "Send your payment from any Canadian bank. Please include your name and invoice number in the transfer message.",
        },
        {
          id: "card",
          title: "Credit Card & Wallet",
          desc: "Pay securely by Visa, Mastercard, or Amex via Stripe's encrypted checkout. Apple Pay and Google Pay are also accepted through this option.",
        },
        {
          id: "paypal",
          title: "PayPal",
          desc: "Settle your invoice after your session with PayPal. The button below opens Natalia's PayPal page, where you enter the amount shown on your invoice.",
        },
      ],
      copyAddress: "Copy address",
      copied: "Copied!",
      paypalCta: "Pay with PayPal",
      amountLabel: "Amount on your invoice (CAD)",
      amountPlaceholder: "e.g. 100.00",
      amountError: "Please enter a valid amount greater than $0.",
      payInvoice: "Pay",
      loading: "Opening secure checkout…",
      errorToast: "Something went wrong starting checkout. Please try again.",
      invoiceNote: "Settling an invoice? The amount shown on it is exactly what you pay. Nothing more.",
      secureNote: "Card and wallet payments are processed securely by Stripe. Your details never touch this site.",
      ratherPre: "Haven't started yet?",
      ratherLink: "Book a free discovery call",
      ratherPost: "first, since payment only comes after sessions.",
    },
    paymentStatus: {
      confirmingTitle: "Confirming your payment…",
      confirmingText: "One moment while I check with Stripe.",
      confirmedTitle: "You're all set. Payment confirmed",
      confirmedText: "Thank you. Choose a time for your next session.",
      bookSession: "Book Your Session Time",
      backHome: "Back to home",
      pendingTitle: "Almost there",
      pendingPre: "I couldn't confirm the payment just yet. If your card was charged, you'll receive a receipt by email shortly, so no need to pay again. Questions? Email",
      pendingPost: ".",
      cancelTitle: "No charge was made",
      cancelText: "The checkout was cancelled and nothing was billed. Whenever you're ready, your options will be right where you left them.",
      cancelRetry: "Return to Payments",
      cancelAlt: "Or book a free discovery call instead",
    },
    about: {
      kicker: "About Natalia",
      title: "Someone who truly gets how your brain works",
      intro: "Coaching adults and teens with ADHD and executive dysfunction isn't just my specialty. It's the heart of my practice. My style is warm, collaborative, and deeply practical: less \"try harder\", more \"let's build something that finally works for you\".",
      bookCta: "Book a Free Discovery Call",
      portraitAlt: "Portrait of Natalia Peña, life coach",
      missionKicker: "My Mission",
      approachKicker: "My Approach",
      whyTitle: "Why ADHD & executive dysfunction?",
      whyP1: "Of all the areas I coach, ADHD and executive dysfunction are closest to my heart. Again and again, I have watched brilliant, capable adults and teens blame themselves for struggles that were never about effort or intelligence. They were about systems built for a different kind of brain.",
      whyP2: "That conviction shapes every session: no judgment, no generic advice, and no shame about the unfinished projects. Just a collaborative plan, practical tools, and steady encouragement while you build focus, structure, and confidence that last.",
      podcast: {
        title: "Also on YouTube: Life in HD",
        text: "I host Life in HD, a podcast born from my own late ADHD diagnosis: real conversations and practical strategies for living in full expression.",
        link: "Watch the episodes",
      },
      credsKicker: "Credentials",
      credsTitle: "Certified, and accountable",
      credsIntro: "My coaching is grounded in recognized professional training and membership.",
      methodKicker: "Methodology",
      methodTitle: "Four chapters, one calmer life",
      cta: {
        title: "Feel like Natalia might be your person?",
        subtitle: "Find out in a free discovery call: warm, honest, and completely pressure-free.",
      },
    },
    testimonialsPage: {
      kicker: "Testimonials",
      title: "A wall of kind words",
      intro: "In their own words: clients who came in overwhelmed and left with systems, focus, and a kinder understanding of their own brains.",
      filters: { all: "All stories", adhd: "ADHD", projects: "Finishing Projects", wedding: "Wedding Planning" },
      cta: {
        title: "Your story could be next",
        subtitle: "Start with a free discovery call and see what changes when the plan finally fits your brain.",
      },
      formKicker: "Share your experience",
      formTitle: "Leave a kind word",
      formIntro: "Worked with Natalia? Your story could be the encouragement someone else needs to take the first step.",
      nameLabel: "Your name",
      namePlaceholder: "e.g. Alex",
      categoryLabel: "What did you work on?",
      otherLabel: "Something else",
      customCategoryLabel: "Name your own category (optional)",
      customCategoryPlaceholder: "e.g. Confidence",
      emailLabel: "Email (optional, never shown)",
      messageLabel: "Your story",
      messagePlaceholder: "What changed for you?",
      photoLabel: "Your photo (optional)",
      photoHint: "Add a photo to show next to your story. JPG, PNG or WebP, up to 5 MB.",
      photoRemove: "Remove",
      photoError: "Please choose an image file (JPG, PNG, WebP or GIF) under 5 MB.",
      submit: "Send my story",
      sending: "Sending…",
      successTitle: "Thank you, truly.",
      successText: "Natalia reads and lovingly reviews every story before it appears on the wall.",
      errorToast: "Something went wrong. Please try again.",
    },
    admin: {
      title: "Story Inbox",
      subtitle: "Read new kind words and publish the ones meant for the wall.",
      signInTitle: "Coach sign-in",
      emailLabel: "Email",
      passwordLabel: "Password",
      signIn: "Sign in",
      signingIn: "Signing in…",
      pending: "Pending review",
      published: "On the wall",
      emptyPending: "No stories waiting. The inbox is clear.",
      emptyPublished: "Nothing published yet.",
      publish: "Publish",
      unpublish: "Unpublish",
      discard: "Discard",
      signOut: "Sign out",
      loginError: "Couldn't sign in. Check your email and password.",
      actionError: "Something went wrong. Try again.",
      translate: "Translate",
      translating: "Translating…",
      versionEn: "English",
      versionEs: "Spanish",
      edit: "Edit",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
    },
    podcastPage: {
      kicker: "Podcast & Videos",
      title: "Life in HD with Natalia Peña",
      intro:
        "A podcast about clarity, self-discovery, and embracing who you were always meant to be. After a late ADHD diagnosis, Natalia realized her struggles weren't personal failures. They were the result of trying to fit into a world that wasn't built for her brain. Through real conversations, insights, and practical strategies, she helps you reframe your mindset and build a life that works for you.",
      schedule: "New episode every 3rd Monday · 11:30am PT / 2:30pm ET",
      subscribe: "Subscribe on YouTube",
      episodeWord: "Episode",
      play: "Play episode",
      watchOn: "Watch on YouTube",
      allEpisodes: "All episodes",
      comingSoonPre: "New episodes coming soon. ",
      comingSoonLink: "subscribe on YouTube",
      comingSoonPost: " to stay in the loop.",
    },
    episodes: {
      ep6: {
        title: "Loving Your Brain",
        desc: "Loving your brain; discovering neurodivergence; turning negatives into positives, with guest Dr. Pat Bacilli.",
      },
      ep5: {
        title: "Finding Your Tribe",
        desc: "Community is essential for healing and growth. A conversation on finding your tribe: people who see you, support you, and walk with you.",
      },
      ep4: {
        title: "Routines, Rituals & Real Life",
        desc: "Routine, rituals, real life, and how to build balance with a neurodivergent brain.",
      },
      ep3: {
        title: "Building Your Brain Toolbox",
        desc: "Creating your Brain Toolbox: the practical strategies and supports that make everyday life easier.",
      },
      ep2: {
        title: "What It Means to Be Neurodivergent",
        desc: "Understanding how your brain works, embracing your unique wiring, and creating a life that honors your mind.",
      },
      ep1: {
        title: "Where It All Began",
        desc: "The very first episode of Life in HD: the conversation that started it all.",
      },
    },
  },

  es: {
    nav: {
      home: "Inicio",
      services: "Servicios",
      about: "Sobre Mí",
      testimonials: "Testimonios",
      podcast: "Podcast y Videos",
      booking: "Reservar",
      bookCall: "Reserva tu Consulta Gratuita",
      bookCallFull: "Reserva tu Consulta Gratuita",
    },
    footer: {
      kicker: "El primer paso es gratis",
      titlePre: "Trabajemos",
      titleEm: "con",
      titlePost: "tu cerebro, no en su contra",
      cta: "Reserva tu Consulta Gratuita",
      tagline:
        "Coaching para adultos y adolescentes con TDAH y disfunción ejecutiva: sistemas, enfoque y confianza que se ajustan a tu cerebro.",
      explore: "Explorar",
      links: {
        home: "Inicio",
        services: "Servicios",
        booking: "Reservar una Llamada",
        payments: "Cómo Pagar",
        about: "Sobre Natalia",
        testimonials: "Testimonios",
      },
      connect: "Conecta",
      creds: "Miembro de ICF · Coach de Vida Certificada",
      copyright: "Copyright © 2026 Natalia Life Coach. Todos los Derechos Reservados.",
      note: "Coaching cálido e informado en TDAH · Sesiones en línea",
    },
    marquee: {
      items: [
        "Función Ejecutiva",
        "Creación de Sistemas",
        "Enfoque",
        "Autocompasión",
        "Crecimiento Sostenible",
        "Coaching Informado en TDAH",
      ],
    },
    bio: {
      mission:
        "Mi misión como Coach de Vida es empoderar e inspirar a mis clientes a vivir una vida auténtica, satisfactoria y alineada con sus valores. Creo que todo el mundo tiene el potencial de crear un cambio positivo en sus vidas, y estoy aquí para apoyarte en tu viaje hacia el crecimiento y la transformación personal.",
      approach:
        "Adopto un enfoque colaborativo y centrado en soluciones para el coaching. Te ayudaré a identificar tus fortalezas, superar obstáculos y crear un plan práctico que se ajuste a cómo realmente funciona tu cerebro. Ofrezco sesiones de coaching individuales para adultos y adolescentes con TDAH y disfunción ejecutiva.",
    },
    home: {
      heroBadge: "Coaching de TDAH y Función Ejecutiva",
      heroL1: "¿Batallas con el enfoque,",
      heroL2: "la constancia,",
      heroL3: "o el agobio?",
      heroAccent: "No te falta fuerza de voluntad.",
      heroSubPre: "Tu cerebro simplemente funciona diferente, y ahí es exactamente donde entro yo. Soy Natalia, coach de vida certificada, y ayudo a adultos y adolescentes con TDAH y disfunción ejecutiva a construir ",
      heroSubStrong: "sistemas, enfoque y confianza",
      heroSubPost: " que de verdad se ajustan a su forma de pensar.",
      heroCtaPrimary: "Reserva tu Consulta Gratuita",
      heroCtaSecondary: "Explorar Servicios",
      heroMicro: "Gratis · Sin pago requerido · En línea, desde cualquier lugar",
      portraitRole: "Miembro de ICF · Coach Certificada",
      painKicker: "¿Te suena familiar?",
      painTitle: "La fuerza de voluntad nunca fue el problema",
      painIntro:
        "Los consejos de productividad convencionales fueron diseñados para cerebros convencionales. Si las agendas, las apps y el \"solo hazlo\" te han fallado, no hay nada malo contigo. Las herramientas no fueron hechas para ti.",
      painHelpLabel: "Cómo ayuda el coaching",
      pain: {
        time: {
          title: "Ceguera del Tiempo",
          struggle: "Las horas se esfuman. Las fechas límite te tienden emboscadas. No es descuido, es cómo tu cerebro percibe el tiempo.",
          reframe: "Construyo anclas de tiempo externas: temporizadores visuales, colchones y ritmos que hacen el tiempo visible.",
        },
        initiation: {
          title: "Iniciación de Tareas",
          struggle: "Sabes exactamente qué hacer. Simplemente... no puedes empezar. Eso es parálisis de iniciación, no pereza.",
          reframe: "Diseño primeros pasos sin fricción y estrategias de acompañamiento que hacen que empezar se sienta seguro.",
        },
        memory: {
          title: "Memoria de Trabajo",
          struggle: "Entras a una habitación y olvidas por qué. Pierdes el hilo a mitad de tarea. Tus notas mentales se caen.",
          reframe: "Creo sistemas de captura externos para que nada importante viva solo en tu cabeza.",
        },
      },
      chaptersKicker: "Cómo funciona el coaching",
      chaptersTitle: "Una ruta amable, en cuatro capítulos",
      watch: {
        kicker: "Mira y Escucha",
        title: "Life in HD, el podcast",
        intro:
          "Conversaciones reales sobre TDAH, autodescubrimiento y cómo construir una vida que se ajuste a tu cerebro, con Natalia.",
        seeAll: "Ver Todos los Episodios",
        subscribe: "Suscríbete en YouTube",
      },
      quotesKicker: "Palabras de clientes",
      quotesTitle: "Personas reales. Resultados reales.",
      quotesLink: "Leer todas las historias",
    },
    chapters: [
      { num: "01", title: "Comprender", text: "Mapea cómo funciona realmente tu cerebro único: tus fortalezas, tus puntos de fricción y los patrones detrás del agobio." },
      { num: "02", title: "Desempacar", text: "Separa las metas que importan del ruido, y divídelas en piezas lo bastante pequeñas para que un cerebro real pueda empezar." },
      { num: "03", title: "Sistematizar", text: "Diseña sistemas amigables con el TDAH para el tiempo, las tareas y el seguimiento, construidos alrededor de tu vida, no en su contra." },
      { num: "04", title: "Sostener", text: "Cultiva constancia con responsabilidad y autocompasión, para que el progreso sobreviva incluso tus semanas más duras." },
    ],
    ctaBanner: {
      kicker: "Gratis · Sin pago · Sin presión",
      title: "¿Quieres construir un sistema que trabaje a favor de tu cerebro?",
      subtitle: "Empieza con una consulta gratuita y sin presión. Sin pago, sin compromiso. Solo una conversación.",
      button: "Reserva tu Consulta Gratuita",
    },
    testimonialsData: {
      lucas: {
        quote: "Natalia fue fundamental para ayudarme a sobrellevar mi diagnóstico de TDAH. Me dio un plan claro y estrategias efectivas que me permitieron completar proyectos con los que antes batallaba.",
        catLabel: "TDAH",
      },
      daniela: {
        quote: "Por culpa de mi TDAH no lograba terminar ningún proyecto, no encontraba el tiempo para trabajar en mis sueños, pero Natalia realmente me ayudó a enfocarme en mí, ¡y a entender de verdad mi cerebro!",
        catLabel: "Completar Proyectos",
      },
      "given-cassia": {
        quote: "Natalia nos ayudó a organizarnos en el proceso de planificación de nuestra boda. Nos dio consejos y herramientas útiles para mantenernos organizados. Nos encantó lo flexible y genuina que fue al empoderarnos en nuestro proceso.",
        catLabel: "Planificación de Boda",
      },
      rosie: {
        quote: "Trabajar con Natalia ha sido un cambio total para mí. Su comprensión del TDAH y su enfoque compasivo me han ayudado a navegar desafíos que alguna vez creí insuperables.",
        catLabel: "TDAH",
      },
    },
    services: {
      kicker: "Servicios",
      title: "Coaching diseñado para cerebros que piensan diferente",
      intro: "Ofrezco sesiones de coaching individuales para adultos y adolescentes con TDAH y disfunción ejecutiva. Mi enfoque es construir sistemas prácticos para la concentración, el seguimiento y la vida diaria que realmente se ajusten a cómo funciona tu cerebro.",
      bookCta: "Reserva tu Consulta Gratuita",
      flagshipBadge: "Especialidad",
      efLabel: "Para la disfunción ejecutiva",
      offeringsIntro: "Dentro de mis sesiones individuales, trabajo en estas áreas según lo que más te importe.",
      offerings: [
        {
          id: "adhd-coaching",
          title: "Coaching de TDAH y Función Ejecutiva",
          flagship: true,
          description: "Mi especialidad. Coaching individual para adultos y adolescentes cuyos cerebros resisten los consejos de productividad convencionales, construyendo sistemas de enfoque, seguimiento y vida diaria diseñados alrededor de cómo realmente funciona tu cerebro.",
          helps: "Apunta directamente a la disfunción ejecutiva: iniciación de tareas, ceguera del tiempo, memoria de trabajo, organización y regulación emocional.",
        },
        {
          id: "stress-management",
          title: "Manejo del Estrés",
          description: "Aprende a detectar la sobrecarga antes de que se convierta en apagón, y construye rituales de recuperación que calmen un sistema nervioso que siempre va a máxima velocidad.",
          helps: "Reduce el agobio crónico que hace que cada tarea ejecutiva se sienta diez veces más pesada.",
        },
        {
          id: "work-life-balance",
          title: "Equilibrio Trabajo-Vida",
          description: "Crea límites y transiciones que sí se sostienen, especialmente cuando a tu cerebro le cuesta desconectarse, cambiar de tarea o cambiar de ritmo.",
          helps: "Suaviza la dificultad para cambiar de tarea y los choques de hiperfoco que mezclan el trabajo y el descanso.",
        },
        {
          id: "career-transitions",
          title: "Transiciones Profesionales",
          description: "Navega cambios de empleo, ascensos o caminos completamente nuevos con un plan estructurado, convirtiendo decisiones grandes y vagas en pasos claros y realizables.",
          helps: "Convierte la parálisis de decisiones en una secuencia de acciones pequeñas y agendadas, con responsabilidad integrada.",
        },
        {
          id: "personal-growth",
          title: "Crecimiento Personal",
          description: "Reconecta con las metas y los sueños que sigues posponiendo, y reconstruye la confianza en ti después de años de sentirte 'en retraso'.",
          helps: "Reconstruye la confianza que la disfunción ejecutiva erosiona silenciosamente, una promesa cumplida contigo a la vez.",
        },
      ],
      paySectionText: "Las sesiones se facturan después de cada llamada completada. Nada se cobra por adelantado. Así funciona, paso a paso.",
      payLinkLabel: "Cómo Funciona el Pago",
      notSurePre: "¿No sabes si encaja?",
      notSureLink: "Reserva una consulta gratuita",
      notSurePost: "y lo deciden juntos.",
      faqKicker: "Preguntas",
      faqTitle: "Preguntas frecuentes, respuestas honestas",
      faqs: [
        { q: "¿Necesito un diagnóstico oficial de TDAH para trabajar con Natalia?", a: "No. Si batallas con el enfoque, el seguimiento, la organización o el agobio, con diagnóstico o sin él, eres bienvenida/o. El coaching se trata de construir sistemas que funcionen para tu cerebro, desde donde sea que partas." },
        { q: "¿Qué pasa en la consulta gratuita?", a: "Una conversación relajada y amable. Compartes lo que ha sido difícil, Natalia explica cómo funciona el coaching, y ambos deciden si encaja bien. Sin pago, sin presión, sin preparación." },
        { q: "¿Las sesiones son en línea o presenciales?", a: "Las sesiones se realizan en línea por videollamada, para que puedas conectarte desde donde te sientas más cómoda/o. Esto funciona especialmente bien para adolescentes y adultos ocupados." },
        { q: "¿Cómo funciona el pago?", a: "Nada se cobra por adelantado. Después de cada sesión completada, Natalia te envía una factura por correo con un enlace a la página de pagos, donde puedes pagar por Interac e-Transfer, tarjeta de crédito o Apple Pay / Google Pay." },
        { q: "¿Qué pasa si necesito cancelar o reprogramar?", a: "La vida pasa. Puedes reprogramar o cancelar cualquier sesión sin costo con al menos 24 horas de aviso desde tu enlace de reserva. Las cancelaciones o reprogramaciones hechas con menos de 24 horas de anticipación a la sesión tendrán un cargo por cancelación tardía de $50 CAD." },
      ],
    },
    booking: {
      kicker: "Reservas",
      title: "Elige una hora. Eso es todo.",
      intro: "Un solo paso, sin cuentas, sin formularios que completar. Elige tu llamada abajo y toma el horario que te convenga.",
      introFree: "La consulta es completamente gratuita.",
      chips: ["Cero presión, cero preparación", "Sin pago requerido", "Videollamada en línea"],
      tabs: {
        discovery: { label: "Consulta Gratuita", hint: "30 min · gratis · sin pago" },
        session: { label: "Sesión de Coaching", hint: "60 min · para clientes actuales" },
      },
      fallbackPre: "¿Problemas con el calendario?",
      fallbackLink: "Escríbenos a",
      fallbackPost: "y encuentro un horario contigo.",
    },
    payments: {
      kicker: "Pagos",
      title: "Cómo pagar tu factura",
      intro: "Nada se cobra por adelantado. El pago se realiza después de cada sesión completada, por factura. Así de simple.",
      stepsTitle: "Cómo funciona",
      steps: [
        "Reserva una consulta gratuita desde el calendario en línea.",
        "Natalia te envía un contrato (PDF) por correo para firmar.",
        "Reserva una o más sesiones de 60 minutos a tu propio ritmo.",
        "Después de cada sesión completada, Natalia te envía una factura por correo con un enlace a esta página.",
        "Paga con tu método preferido, abajo.",
      ],
      optionsTitle: "Elige cómo pagar",
      optionsIntro: "Usa la que te resulte más fácil. El monto de tu factura es todo lo que pagas.",
      options: [
        {
          id: "interac",
          title: "Interac e-Transfer",
          desc: "Envía tu pago desde cualquier banco canadiense. Incluye tu nombre y número de factura en el mensaje de la transferencia.",
        },
        {
          id: "card",
          title: "Tarjeta de Crédito y Billetera",
          desc: "Paga de forma segura con Visa, Mastercard o Amex a través del checkout encriptado de Stripe. También se aceptan Apple Pay y Google Pay en esta opción.",
        },
        {
          id: "paypal",
          title: "PayPal",
          desc: "Paga tu factura después de tu sesión con PayPal. El botón de abajo abre la página de PayPal de Natalia, donde ingresas el monto indicado en tu factura.",
        },
      ],
      copyAddress: "Copiar dirección",
      copied: "¡Copiado!",
      paypalCta: "Pagar con PayPal",
      amountLabel: "Monto de tu factura (CAD)",
      amountPlaceholder: "ej. 100.00",
      amountError: "Ingresa un monto válido mayor a $0.",
      payInvoice: "Pagar",
      loading: "Abriendo checkout seguro…",
      errorToast: "Algo salió mal al iniciar el pago. Inténtalo de nuevo, por favor.",
      invoiceNote: "¿Pagando una factura? El monto indicado es exactamente lo que pagas. Nada más.",
      secureNote: "Los pagos con tarjeta y billetera se procesan de forma segura con Stripe. Tus datos nunca tocan este sitio.",
      ratherPre: "¿Aún no empiezas?",
      ratherLink: "Reserva una consulta gratuita",
      ratherPost: "primero, ya que el pago solo llega después de las sesiones.",
    },
    paymentStatus: {
      confirmingTitle: "Confirmando tu pago…",
      confirmingText: "Un momento mientras verifico con Stripe.",
      confirmedTitle: "Todo listo. Pago confirmado",
      confirmedText: "Gracias. Elige el horario de tu próxima sesión.",
      bookSession: "Reserva el Horario de tu Sesión",
      backHome: "Volver al inicio",
      pendingTitle: "Casi listo",
      pendingPre: "Aún no pude confirmar el pago. Si tu tarjeta fue cargada, recibirás un recibo por correo en breve, así que no necesitas pagar de nuevo. ¿Dudas? Escribe a",
      pendingPost: ".",
      cancelTitle: "No se realizó ningún cargo",
      cancelText: "El checkout fue cancelado y no se facturó nada. Cuando quieras, tus opciones seguirán justo donde las dejaste.",
      cancelRetry: "Volver a Pagos",
      cancelAlt: "O reserva una consulta gratuita",
    },
    about: {
      kicker: "Sobre Natalia",
      title: "Alguien que realmente entiende cómo funciona tu cerebro",
      intro: "El coaching para adultos y adolescentes con TDAH y disfunción ejecutiva no es solo mi especialidad. Es el corazón de mi práctica. Mi estilo es cálido, colaborativo y profundamente práctico: menos \"esfuérzate más\", más \"construyamos algo que por fin funcione para ti\".",
      bookCta: "Reserva tu Consulta Gratuita",
      portraitAlt: "Retrato de Natalia Peña, coach de vida",
      missionKicker: "Mi Misión",
      approachKicker: "Mi Enfoque",
      whyTitle: "¿Por qué TDAH y disfunción ejecutiva?",
      whyP1: "De todas las áreas en las que trabajo, el TDAH y la disfunción ejecutiva son las más cercanas a mi corazón. Una y otra vez, he visto a adultos y adolescentes brillantes y capaces culparse por dificultades que nunca fueron cuestión de esfuerzo o inteligencia. Eran sistemas construidos para otro tipo de cerebro.",
      whyP2: "Esa convicción da forma a cada sesión: sin juicios, sin consejos genéricos y sin vergüenza por los proyectos sin terminar. Solo un plan colaborativo, herramientas prácticas y aliento constante mientras construyes enfoque, estructura y confianza que perduran.",
      podcast: {
        title: "También en YouTube: Life in HD",
        text: "Conduzco Life in HD, un podcast que nació de mi propio diagnóstico tardío de TDAH: conversaciones reales y estrategias prácticas para vivir en plena expresión.",
        link: "Ver los episodios",
      },
      credsKicker: "Credenciales",
      credsTitle: "Certificada y comprometida",
      credsIntro: "Mi coaching se apoya en formación profesional y membresías reconocidas.",
      methodKicker: "Metodología",
      methodTitle: "Cuatro capítulos, una vida más tranquila",
      cta: {
        title: "¿Crees que Natalia podría ser quien buscas?",
        subtitle: "Descúbrelo en una consulta gratuita: cálida, honesta y completamente sin presión.",
      },
    },
    testimonialsPage: {
      kicker: "Testimonios",
      title: "Un muro de palabras amables",
      intro: "En sus propias palabras: clientes que llegaron agobiados y se fueron con sistemas, enfoque y una comprensión más amable de sus propios cerebros.",
      filters: { all: "Todas las historias", adhd: "TDAH", projects: "Completar Proyectos", wedding: "Planificación de Boda" },
      cta: {
        title: "Tu historia podría ser la siguiente",
        subtitle: "Empieza con una consulta gratuita y descubre qué cambia cuando el plan por fin se ajusta a tu cerebro.",
      },
      formKicker: "Comparte tu experiencia",
      formTitle: "Deja unas palabras amables",
      formIntro: "¿Trabajaste con Natalia? Tu historia podría ser el empujón que alguien más necesita para dar el primer paso.",
      nameLabel: "Tu nombre",
      namePlaceholder: "ej. Alex",
      categoryLabel: "¿En qué trabajaron?",
      otherLabel: "Otra cosa",
      customCategoryLabel: "Ponle nombre a tu categoría (opcional)",
      customCategoryPlaceholder: "ej. Confianza",
      emailLabel: "Correo (opcional, nunca se muestra)",
      messageLabel: "Tu historia",
      messagePlaceholder: "¿Qué cambió para ti?",
      photoLabel: "Tu foto (opcional)",
      photoHint: "Agrega una foto para mostrar junto a tu historia. JPG, PNG o WebP, hasta 5 MB.",
      photoRemove: "Quitar",
      photoError: "Elige un archivo de imagen (JPG, PNG, WebP o GIF) de menos de 5 MB.",
      submit: "Enviar mi historia",
      sending: "Enviando…",
      successTitle: "Gracias, de verdad.",
      successText: "Natalia lee y revisa con cariño cada historia antes de que aparezca en el muro.",
      errorToast: "Algo salió mal. Inténtalo de nuevo.",
    },
    admin: {
      title: "Buzón de Historias",
      subtitle: "Lee las nuevas palabras amables y publica las que van al muro.",
      signInTitle: "Acceso de la coach",
      emailLabel: "Correo",
      passwordLabel: "Contraseña",
      signIn: "Entrar",
      signingIn: "Entrando…",
      pending: "Pendientes de revisión",
      published: "En el muro",
      emptyPending: "No hay historias esperando. El buzón está despejado.",
      emptyPublished: "Aún no hay nada publicado.",
      publish: "Publicar",
      unpublish: "Despublicar",
      discard: "Descartar",
      signOut: "Salir",
      loginError: "No se pudo entrar. Revisa tu correo y contraseña.",
      actionError: "Algo salió mal. Inténtalo de nuevo.",
      translate: "Traducir",
      translating: "Traduciendo…",
      versionEn: "Inglés",
      versionEs: "Español",
      edit: "Editar",
      save: "Guardar",
      saving: "Guardando…",
      cancel: "Cancelar",
    },
    podcastPage: {
      kicker: "Podcast y Videos",
      title: "Life in HD with Natalia Peña",
      intro:
        "Un podcast sobre claridad, autodescubrimiento y abrazar quien siempre estuviste destinada/o a ser. Tras un diagnóstico tardío de TDAH, Natalia entendió que sus dificultades no eran fracasos personales. Eran el resultado de intentar encajar en un mundo que no fue construido para su cerebro. A través de conversaciones reales, aprendizajes y estrategias prácticas, te ayuda a reformular tu mentalidad y construir una vida que funcione para ti.",
      schedule: "Nuevo episodio cada tercer lunes · 11:30am PT / 2:30pm ET",
      subscribe: "Suscríbete en YouTube",
      episodeWord: "Episodio",
      play: "Reproducir episodio",
      watchOn: "Ver en YouTube",
      allEpisodes: "Todos los episodios",
      comingSoonPre: "Nuevos episodios próximamente. ",
      comingSoonLink: "suscríbete en YouTube",
      comingSoonPost: " para mantenerte al tanto.",
    },
    episodes: {
      ep6: {
        title: "Amar Tu Cerebro",
        desc: "Amar tu cerebro; descubrir la neurodivergencia; convertir lo negativo en positivo, con la invitada Dra. Pat Bacilli.",
      },
      ep5: {
        title: "Encontrar Tu Tribu",
        desc: "La comunidad es esencial para sanar y crecer. Una conversación sobre encontrar tu tribu: personas que te ven, te apoyan y caminan contigo.",
      },
      ep4: {
        title: "Rutinas, Rituales y Vida Real",
        desc: "Rutinas, rituales y vida real: cómo construir equilibrio con un cerebro neurodivergente.",
      },
      ep3: {
        title: "Construyendo Tu Caja de Herramientas Mental",
        desc: "Creando tu Caja de Herramientas Mental: las estrategias y apoyos prácticos que facilitan la vida diaria.",
      },
      ep2: {
        title: "Qué Significa Ser Neurodivergente",
        desc: "Entender cómo funciona tu cerebro, abrazar tu forma única de ser y crear una vida que honre tu mente.",
      },
      ep1: {
        title: "Donde Todo Comenzó",
        desc: "El primer episodio de Life in HD: la conversación con la que todo empezó.",
      },
    },
  },
};
